import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Upload, X, Palette, Building2, FileText } from 'lucide-react';import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/simple-toaster';
import { useOrganizationBranding, useUpdateBranding, useRemoveLogo } from '@/hooks/use-branding';
import { Skeleton } from '@/app/components/ui/skeleton';

const DEFAULT_PRIMARY_COLOR = '#0060FF';
const DEFAULT_SECONDARY_COLOR = '#1a1a2e';
const MAX_LOGO_BYTES = 10 * 1024 * 1024;

/**
 * Compute relative luminance for a hex color.
 * Returns a value between 0 (darkest) and 1 (lightest).
 */
function getLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

interface BrandingFormState {
  primaryColor: string;
  secondaryColor: string;
  address: string;
  phone: string;
  email: string;
  website: string;
}

export interface BrandingCardRef {
  isDirty: () => boolean;
  save: () => Promise<void>;
  reset: () => void;
}

interface BrandingCardProps {
  onDirtyChange?: (dirty: boolean) => void;
}

export const BrandingCard = forwardRef<BrandingCardRef, BrandingCardProps>(function BrandingCard(props, ref) {
  const { data: branding, isLoading } = useOrganizationBranding();
  const updateBranding = useUpdateBranding();
  const removeLogo = useRemoveLogo();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [removeLogoFlag, setRemoveLogoFlag] = useState(false);

  const [form, setForm] = useState<BrandingFormState>({
    primaryColor: '',
    secondaryColor: '',
    address: '',
    phone: '',
    email: '',
    website: '',
  });

  const [hasChanges, setHasChanges] = useState(false);

  const { onDirtyChange } = props;

  useImperativeHandle(ref, () => ({
    isDirty: () => hasChanges,
    save: handleSave,
    reset: handleReset,
  }));

  // Populate form from branding data
  useEffect(() => {
    if (branding) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm({
        primaryColor: branding.primary_color || DEFAULT_PRIMARY_COLOR,
        secondaryColor: branding.secondary_color || DEFAULT_SECONDARY_COLOR,
        address: branding.address || '',
        phone: branding.phone || '',
        email: branding.email || '',
        website: branding.website || '',
      });
      setLogoPreview(branding.logo_url || null);
      setLogoFile(null);
      setRemoveLogoFlag(false);
    }
  }, [branding]);

  // Track changes
  useEffect(() => {
    if (!branding) return;

    const origPrimary = branding.primary_color || DEFAULT_PRIMARY_COLOR;
    const origSecondary = branding.secondary_color || DEFAULT_SECONDARY_COLOR;

    const changed =
      form.primaryColor !== origPrimary ||
      form.secondaryColor !== origSecondary ||
      form.address !== (branding.address || '') ||
      form.phone !== (branding.phone || '') ||
      form.email !== (branding.email || '') ||
      form.website !== (branding.website || '') ||
      logoFile !== null ||
      removeLogoFlag;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasChanges(changed);
    onDirtyChange?.(changed);
  }, [form, branding, logoFile, removeLogoFlag, onDirtyChange]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('File too large', { description: 'Logo must be under 10MB.' });
      return;
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type', { description: 'Logo must be PNG or JPG.' });
      return;
    }

    setLogoFile(file);
    setRemoveLogoFlag(false);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogoFlag(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleResetColors = () => {
    setForm((prev) => ({
      ...prev,
      primaryColor: DEFAULT_PRIMARY_COLOR,
      secondaryColor: DEFAULT_SECONDARY_COLOR,
    }));
  };

  const handleReset = () => {
    if (branding) {
      setForm({
        primaryColor: branding.primary_color || DEFAULT_PRIMARY_COLOR,
        secondaryColor: branding.secondary_color || DEFAULT_SECONDARY_COLOR,
        address: branding.address || '',
        phone: branding.phone || '',
        email: branding.email || '',
        website: branding.website || '',
      });
      setLogoPreview(branding.logo_url || null);
      setLogoFile(null);
      setRemoveLogoFlag(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    try {
      // If removing logo, do that first
      if (removeLogoFlag && branding?.logo_url) {
        await removeLogo.mutateAsync();
      }

      // Build the update payload
      const payload: Record<string, string | File> = {};

      if (logoFile) {
        payload.logo = logoFile;
      }

      const origPrimary = branding?.primary_color || DEFAULT_PRIMARY_COLOR;
      const origSecondary = branding?.secondary_color || DEFAULT_SECONDARY_COLOR;

      // Send color values — empty string to clear custom color, hex to set
      if (form.primaryColor !== origPrimary) {
        payload.primary_color = form.primaryColor === DEFAULT_PRIMARY_COLOR ? '' : form.primaryColor;
      }
      if (form.secondaryColor !== origSecondary) {
        payload.secondary_color = form.secondaryColor === DEFAULT_SECONDARY_COLOR ? '' : form.secondaryColor;
      }

      // Always send text fields so backend has the latest
      payload.address = form.address;
      payload.phone = form.phone;
      payload.email = form.email;

      // Auto-prefix https:// on bare URLs
      let website = form.website.trim();
      if (website && !/^https?:\/\//i.test(website)) {
        website = `https://${website}`;
      }
      payload.website = website;

      await updateBranding.mutateAsync(payload as unknown as import('@/lib/types').BrandingUpdatePayload);
    } catch (err: unknown) {
      // Extract field-level validation errors from the API response
      const axiosError = err as { response?: { data?: Record<string, string[]> } };
      const fieldErrors = axiosError?.response?.data;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const messages = Object.entries(fieldErrors)
          .map(([field, errors]) => {
            const label = field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            const msgs = Array.isArray(errors) ? errors.join(', ') : String(errors);
            return `${label}: ${msgs}`;
          })
          .join('\n');
        if (messages) {
          toast.error('Validation Error', { description: messages });
          throw err;
        }
      }
      toast.error('Error', {
        description: 'Failed to save branding. Please try again.',
      });
      throw err;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-4">
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Company Branding
          </CardTitle>
          <CardDescription>
            Customize your organization&apos;s logo, colors, and contact information
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Logo Upload Section */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Company Logo</Label>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0 w-32 h-32 border-2 border-dashed border-gray-300 dark:border-border rounded-lg flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-secondary">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Company logo"
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <Upload className="h-8 w-8 mx-auto mb-1" />
                  <span className="text-xs">No logo</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Logo
              </Button>
              {logoPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleRemoveLogo}
                >
                  <X className="h-4 w-4 mr-2" />
                  Remove Logo
                </Button>
              )}
              <p className="text-xs text-gray-500">
                Max 10MB. PNG or JPG.
              </p>
            </div>
          </div>
        </div>

        {/* Brand Colors Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Brand Colors</Label>
            <button
              type="button"
              onClick={handleResetColors}
              className="text-xs text-blue-600 hover:underline"
            >
              Reset to defaults
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Primary Color */}
            <div className="space-y-2">
              <Label htmlFor="primary-color">Primary Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="primary-color"
                  value={form.primaryColor}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, primaryColor: e.target.value }))
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer p-0.5"
                />
                <Input
                  value={form.primaryColor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                      setForm((prev) => ({ ...prev, primaryColor: val }));
                    }
                  }}
                  className="max-w-[120px] font-mono text-sm"
                  maxLength={7}
                  placeholder="#0060FF"
                />
                <div
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: form.primaryColor,
                    color: getLuminance(form.primaryColor) > 0.5 ? '#000' : '#fff',
                  }}
                >
                  Aa
                </div>
              </div>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <Label htmlFor="secondary-color">Secondary Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="secondary-color"
                  value={form.secondaryColor}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, secondaryColor: e.target.value }))
                  }
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer p-0.5"
                />
                <Input
                  value={form.secondaryColor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                      setForm((prev) => ({ ...prev, secondaryColor: val }));
                    }
                  }}
                  className="max-w-[120px] font-mono text-sm"
                  maxLength={7}
                  placeholder="#1a1a2e"
                />
                <div
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-xs font-bold"
                  style={{
                    backgroundColor: form.secondaryColor,
                    color: getLuminance(form.secondaryColor) > 0.5 ? '#000' : '#fff',
                  }}
                >
                  Aa
                </div>
              </div>
            </div>
          </div>

          {/* Color Preview Swatches */}
          <div className="flex items-center gap-3 mt-3">
            <span className="text-sm text-gray-500">Preview:</span>
            <div className="flex gap-1">
              <div
                className="h-6 w-16 rounded-l-md"
                style={{ backgroundColor: form.primaryColor }}
              />
              <div
                className="h-6 w-16 rounded-r-md"
                style={{ backgroundColor: form.secondaryColor }}
              />
            </div>
            <button
              type="button"
              className="px-3 py-1 rounded text-xs font-medium text-white"
              style={{ backgroundColor: form.primaryColor }}
            >
              Button
            </button>
          </div>
        </div>

        {/* Company Information Section */}
        <div className="space-y-3">
          <Label className="text-base font-semibold flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Company Information
          </Label>
          <p className="text-sm text-gray-500">
            This information appears on your PDF documents (bids, invoices, safety reports).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="company-address">Address</Label>
              <Textarea
                id="company-address"
                placeholder="123 Main St&#10;Suite 100&#10;City, State ZIP"
                value={form.address}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, address: e.target.value }))
                }
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-phone">Phone</Label>
              <Input
                id="company-phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={form.phone}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company-email">Email</Label>
              <Input
                id="company-email"
                type="email"
                placeholder="info@company.com"
                value={form.email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="company-website">Website</Label>
              <Input
                id="company-website"
                type="url"
                placeholder="https://company.com"
                value={form.website}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, website: e.target.value }))
                }
              />
              <p className="text-xs text-gray-500">https:// will be added automatically if omitted.</p>
            </div>
          </div>
        </div>

        {/* Letterhead Preview (stretch goal) */}
        <div className="space-y-3">
          <Label className="text-base font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Letterhead Preview
          </Label>
          <div className="border border-gray-200 dark:border-border rounded-lg p-6 bg-white dark:bg-card shadow-sm max-w-lg">
            <div className="flex items-start gap-4 mb-4">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-16 h-16 object-contain flex-shrink-0"
                />
              ) : (
                <div
                  className="w-16 h-16 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: form.primaryColor }}
                >
                  <span
                    className="text-lg font-bold"
                    style={{ color: getLuminance(form.primaryColor) > 0.5 ? '#000' : '#fff' }}
                  >
                    {branding?.name?.charAt(0) || 'H'}
                  </span>
                </div>
              )}
              <div>
                <h3
                  className="font-bold text-lg"
                  style={{ color: form.secondaryColor }}
                >
                  {branding?.name || 'H3ight'}
                </h3>
                {form.address && (
                  <p className="text-xs text-gray-500 whitespace-pre-line">{form.address}</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-0 text-xs text-gray-500 mt-1">
                  {form.phone && <span>{form.phone}</span>}
                  {form.email && <span>{form.email}</span>}
                  {form.website && <span>{form.website}</span>}
                </div>
              </div>
            </div>
            <div
              className="h-1 rounded-full"
              style={{
                background: `linear-gradient(to right, ${form.primaryColor}, ${form.secondaryColor})`,
              }}
            />
            <div className="mt-3 space-y-1">
              <div className="h-2 bg-gray-100 rounded w-full" />
              <div className="h-2 bg-gray-100 rounded w-4/5" />
              <div className="h-2 bg-gray-100 rounded w-3/5" />
            </div>
            <div className="mt-4 flex justify-between text-[10px] text-gray-400">
              <span>Generated by {branding?.name || 'H3ight'}</span>
              <span>Page 1</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
